import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AutoAwesome,
  BusinessCenter,
  SupportAgent,
} from '@mui/icons-material';
import { consultingOffers, consultingSegments, consultingSteps } from '../data/pesquisaInovacao';
import { headerGradient } from '../theme';

const SectionTitle: React.FC<{ children: React.ReactNode; centered?: boolean; width?: number }> = ({
  children,
  centered = false,
  width = 220,
}) => (
  <Typography
    variant="h5"
    sx={{
      fontWeight: 700,
      color: 'primary.main',
      mb: 2,
      position: 'relative',
      pb: 1,
      textAlign: centered ? 'center' : 'left',
      lineHeight: 1.25,
      fontSize: { xs: '1.3rem', sm: '1.45rem', md: '1.5rem' },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: centered ? '50%' : 0,
        transform: centered ? 'translateX(-50%)' : 'none',
        bottom: 0,
        width: { xs: Math.min(width, 220), sm: width },
        height: 3,
        bgcolor: 'secondary.main',
      },
    }}
  >
    {children}
  </Typography>
);

const interestHref = 'https://forms.cloud.microsoft/pages/responsepage.aspx?id=VYz8K3TflEuoWng2wpkRuJdblgQ4KAJFvtuXAUjXaxFUMUZFTFdPQUo0MU9NNE5EVDVQVlE5NzY3Ni4u&route=shorturl';

const PesquisaSolucoesEmpresas: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ pt: { xs: 3, md: 10 }, pb: { xs: 6, md: 8 } }}>
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 3.5, md: 6 },
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.16
          )} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.05rem', md: '2.125rem' },
                lineHeight: 1.16,
                display: 'inline-block',
                position: 'relative',
                pb: 1.5,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  width: { xs: 160, sm: 220 },
                  height: 4,
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
            >
              Soluções Inovadoras para Empresas
            </Typography>

            <Typography sx={{ mt: { xs: 2, md: 3 }, lineHeight: 1.85, maxWidth: 760, fontSize: { xs: '0.96rem', md: '1rem' } }}>
              Consultorias com linguagem executiva, aplicação prática e foco em transformação institucional. A página foi
              organizada para apresentar soluções prioritárias, segmentos atendidos, etapas da consultoria e um fluxo claro
              de conversão para empresas interessadas.
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                mt: 2,
                '& .MuiChip-root': { height: 'auto' },
                '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
              }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip icon={<BusinessCenter />} label="Consultorias institucionais" color="primary" variant="outlined" />
              <Chip icon={<AutoAwesome />} label="Soluções com IA e inovação" color="secondary" variant="outlined" />
              <Chip icon={<SupportAgent />} label="Atendimento orientado ao negócio" color="success" variant="outlined" />
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              sx={{
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                boxShadow: '0 20px 45px rgba(15, 23, 42, 0.09)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1 }}>
                  Atendimento consultivo
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: 'primary.main' }}>
                  Uma jornada pensada para decisões mais seguras
                </Typography>
                <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                  A proposta combina diagnóstico, planejamento e acompanhamento para que a empresa avance com mais clareza
                  na implementação de melhorias, inovação e uso estratégico de tecnologia.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  component="a"
                  href={interestHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 3, fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}
                >
                  Registre seu interesse
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 4, md: 5 } }}>
        <Grid container spacing={3}>
          {consultingOffers.map((offer) => (
            <Grid item xs={12} sm={6} md={3} key={offer.title}>
              <Card
                component={RouterLink}
                to={offer.path}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: { xs: 290, sm: 340, md: 390 },
                  borderRadius: 3,
                  display: 'block',
                  textDecoration: 'none',
                  backgroundImage: `url("${offer.image}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.12)',
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${theme.palette.secondary.main}`,
                    outlineOffset: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.32), rgba(0,0,0,0.9))',
                  }}
                />
                <CardContent
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    height: '100%',
                    p: { xs: 2.4, md: 3 },
                    color: '#fff',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                    {offer.title}
                  </Typography>
                  <Typography sx={{ mt: 1.5, lineHeight: 1.7, color: 'rgba(255,255,255,0.9)' }}>
                    {offer.description}
                  </Typography>
                  <Typography sx={{ mt: 2, fontWeight: 700, color: '#fff' }}>
                    Ver detalhes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            component="a"
            href={interestHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}
          >
            Registre seu interesse
          </Button>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 5.5, md: 7 } }}>
        <SectionTitle centered width={240}>Consultorias personalizadas</SectionTitle>
        <Typography sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', lineHeight: 1.85, mb: { xs: 3, md: 4 } }}>
          Seja qual for o segmento de atuação do seu negócio, o Senac tem a consultoria ideal para alavancar os
          resultados.
        </Typography>

        <Grid container spacing={2.5}>
          {consultingSegments.map((segment) => (
            <Grid item xs={12} sm={6} md={4} key={segment.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                  backgroundColor: alpha(theme.palette.common.white, 0.94),
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                    {segment.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{segment.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 5.5, md: 7 } }}>
        <SectionTitle centered width={260}>Como funciona a consultoria</SectionTitle>
        <Typography sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto', lineHeight: 1.85, mb: { xs: 3, md: 4 } }}>
          Nossa consultoria analisa o dia a dia da sua empresa e oferece suporte em todas as etapas do processo:
        </Typography>

        <Grid container spacing={3}>
          {consultingSteps.map((step) => (
            <Grid item xs={12} md={6} key={step.number}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: alpha(theme.palette.primary.main, 0.12),
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.8, md: 2.5 }, alignItems: 'flex-start', p: { xs: 2.4, md: 3 } }}>
                  <Box
                    sx={{
                      minWidth: { xs: 56, md: 68 },
                      width: { xs: 56, md: 68 },
                      height: { xs: 56, md: 68 },
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      background: headerGradient,
                      boxShadow: '0 14px 28px rgba(0, 58, 112, 0.18)',
                    }}
                  >
                    {step.number}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{step.description}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 5.5, md: 7 } }}>
        <Card
          sx={{
            color: '#fff',
            background: headerGradient,
            boxShadow: '0 24px 50px rgba(0, 58, 112, 0.18)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.5rem' } }}>
                  Pronto para conversar sobre a necessidade da sua empresa?
                </Typography>
                <Typography sx={{ mt: 1.5, lineHeight: 1.8, color: 'rgba(255,255,255,0.88)' }}>
                  Utilize o canal institucional para registrar interesse em consultorias ou para atuar como especialista
                  parceiro.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    component="a"
                    href={interestHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 700 }}
                  >
                    Registre seu interesse
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    component="a"
                    href="mailto:nepe.faculdade@am.senac.br?subject=Cadastro%20de%20consultores"
                    sx={{ fontWeight: 700 }}
                  >
                    Cadastro de consultores
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PesquisaSolucoesEmpresas;
